import { Flame, Shield, Trophy, UserRound } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { getTier } from '@/lib/xp/tiers'

export interface LeaderboardEntry {
  position: number
  customerId: string
  name: string
  anonymous: boolean
  totalXp: number
  currentLevel: number
  currentStreak: number
}

/**
 * Iniciais só existem para quem TEM nome. A api marca `anonymous: true` quando
 * caiu no rótulo posicional ("Aluno #7") — montar iniciais dali produziria "A#"
 * em metade da lista, e pior: daria a entender que "Aluno" é o nome da pessoa.
 */
function getInitials(name: string) {
  const nameParts = name.trim().split(/\s+/)
  if (nameParts.length >= 2) return (nameParts[0][0] + nameParts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

/** Pódio só nas três primeiras posições REAIS — não nas três primeiras linhas. */
const PODIUM_COLORS: Record<number, string> = {
  1: 'bg-yellow-500/15 text-yellow-500 ring-yellow-500/30',
  2: 'bg-slate-400/15 text-slate-400 ring-slate-400/30',
  3: 'bg-amber-700/15 text-amber-600 ring-amber-600/30',
}

function LeaderboardRow({ entry, isMe }: { entry: LeaderboardEntry; isMe: boolean }) {
  const tier = getTier(entry.currentLevel)
  const podio = PODIUM_COLORS[entry.position]

  return (
    <li
      className={cn(
        'flex items-center gap-3 rounded-lg border p-3 transition-colors',
        isMe ? 'border-primary/40 bg-primary/5' : 'border-transparent bg-muted/30',
      )}
    >
      {/*
        `entry.position` vem da api e NÃO é o índice desta lista. A numeração
        roda sobre a população inteira e só depois descarta quem optou por sair,
        então o top 10 pode terminar na #13. Renumerar aqui pelo índice faria a
        lista voltar a discordar do card do dashboard — que é exatamente o bug
        que o W3-5 fechou.
      */}
      <span
        className={cn(
          'flex h-8 w-9 shrink-0 items-center justify-center rounded-md text-sm font-bold tabular-nums',
          podio ? `ring-1 ${podio}` : 'bg-accent text-muted-foreground',
        )}
      >
        {entry.position}
      </span>

      {entry.anonymous ? (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-muted-foreground">
          <UserRound className="h-4 w-4" />
        </span>
      ) : (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
          {getInitials(entry.name)}
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">
          {entry.name}
          {isMe && <span className="ml-2 text-xs font-normal text-primary">você</span>}
        </p>
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Shield className={cn('h-3 w-3', tier.color)} />
          <span>
            Nível {entry.currentLevel} · {tier.name}
          </span>
          {entry.currentStreak > 0 && (
            <span className="flex items-center gap-1">
              <Flame className="h-3 w-3 text-orange-500" />
              {entry.currentStreak}d
            </span>
          )}
        </p>
      </div>

      <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
        {entry.totalXp.toLocaleString('pt-BR')}
        <span className="ml-1 text-xs font-normal text-muted-foreground">XP</span>
      </span>
    </li>
  )
}

export function LeaderboardList({
  entries,
  myPosition,
}: {
  entries: LeaderboardEntry[]
  /** Posição do próprio aluno — vem do quick-stats, não desta lista. */
  myPosition?: number
}) {
  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border p-10 text-center">
        <Trophy className="h-8 w-8 text-muted-foreground/50" />
        <p className="text-sm font-medium text-foreground">Ninguém pontuou ainda</p>
        <p className="max-w-sm text-xs text-muted-foreground">
          O ranking se monta sozinho conforme a turma assiste às aulas, conclui cursos e mantém a
          sequência de dias. Termine uma aula e você já aparece aqui.
        </p>
      </div>
    )
  }

  return (
    <ul className="space-y-2">
      {entries.map((entry) => (
        <LeaderboardRow
          key={entry.customerId}
          entry={entry}
          // Casar por POSIÇÃO e não por id: a posição é um `ROW_NUMBER` sobre a
          // população, então é única por aluno, e o quick-stats já a entrega.
          // O perfil do aluno não expõe o `customer_id` para comparar.
          isMe={myPosition !== undefined && myPosition > 0 && entry.position === myPosition}
        />
      ))}
    </ul>
  )
}

export function LeaderboardListSkeleton({ rows = 10 }: { rows?: number }) {
  return (
    <ul className="space-y-2">
      {Array.from({ length: rows }, (_, i) => (
        <li key={i} className="flex items-center gap-3 rounded-lg bg-muted/30 p-3">
          <Skeleton className="h-8 w-9" />
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-4 w-16" />
        </li>
      ))}
    </ul>
  )
}
